export const segmentNetMinutes = (checkIn) => {
  if (!checkIn?.check_out_time) {
    return null;
  }
  if (checkIn.time_spent != null && checkIn.time_spent !== '') {
    return Number(checkIn.time_spent);
  }
  const gross = Math.max(
    0,
    Math.floor((new Date(checkIn.check_out_time) - new Date(checkIn.check_in_time)) / 60000)
  );
  return Math.max(gross - (Number(checkIn.lunch_minutes) || 0), 0);
};

export const cumulativeWorkedMinutes = (checkIns, userId) => {
  if (!Array.isArray(checkIns) || !userId) {
    return 0;
  }
  return checkIns
    .filter((entry) => entry.user_id === userId && entry.check_out_time)
    .reduce((sum, entry) => sum + (segmentNetMinutes(entry) || 0), 0);
};

export const currentSessionMinutes = (checkInTime, endTime = Date.now()) => {
  if (!checkInTime) {
    return 0;
  }
  const endMs = endTime instanceof Date ? endTime.getTime() : new Date(endTime).getTime();
  return Math.max(0, Math.floor((endMs - new Date(checkInTime).getTime()) / 60000));
};

export const sessionNetMinutes = (checkInTime, lunchMinutes = 0) => {
  const lunch = Math.max(0, Number(lunchMinutes) || 0);
  return Math.max(currentSessionMinutes(checkInTime) - lunch, 0);
};

export const totalWorkedMinutes = (checkIns, userId, activeCheckInTime, lunchMinutes = 0) => {
  const prior = cumulativeWorkedMinutes(checkIns, userId);
  if (!activeCheckInTime) {
    return prior;
  }
  return prior + sessionNetMinutes(activeCheckInTime, lunchMinutes);
};

/** Lunch minutes from pause-checkout form; invalid → 0. */
export const parsePauseLunchMinutes = (tookLunch, lunchMinutesValue) => {
  if (!tookLunch) {
    return 0;
  }
  return Math.max(0, Number.parseInt(lunchMinutesValue, 10) || 0);
};

/** Prefer manual total input; fall back to computed visit total. Null if neither is usable. */
export const resolveVisitTotalMinutes = (timeSpentValue, checkIns, userId, activeCheckInTime) => {
  if (timeSpentValue !== undefined && timeSpentValue !== null && String(timeSpentValue).trim() !== '') {
    const parsed = Number.parseInt(timeSpentValue, 10);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
    return null;
  }
  const computed = totalWorkedMinutes(checkIns, userId, activeCheckInTime);
  return Number.isFinite(computed) ? computed : null;
};

export const toDateTimeLocalValue = (value) => {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

export const fromDateTimeLocalValue = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};

export const sessionEndFromMinutes = (checkInTime, minutes) => {
  if (!checkInTime) {
    return null;
  }
  const parsed = Number.parseInt(minutes, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return new Date(new Date(checkInTime).getTime() + parsed * 60000);
};

export const buildSessionTimeFields = (checkInTime, endTime = new Date()) => {
  if (!checkInTime) {
    return { timeSpent: '', endTimeLocal: '' };
  }
  const end = endTime instanceof Date ? endTime : new Date(endTime);
  const minutes = currentSessionMinutes(checkInTime, end);
  return {
    timeSpent: minutes > 0 ? String(minutes) : '',
    endTimeLocal: toDateTimeLocalValue(end)
  };
};

/** Sum net minutes for completed check-in cycles only (open cycles excluded). */
export const taskCompletedNetMinutes = (checkIns) => {
  if (!Array.isArray(checkIns)) {
    return null;
  }
  const completed = checkIns.filter((entry) => entry.check_out_time);
  if (completed.length === 0) {
    return null;
  }
  return completed.reduce((sum, entry) => sum + (segmentNetMinutes(entry) || 0), 0);
};

/** Sum lunch minutes across segments (matches Time Report aggregate lunch sum). */
export const taskLunchMinutes = (checkIns) => {
  if (!Array.isArray(checkIns) || checkIns.length === 0) {
    return 0;
  }
  return checkIns.reduce((sum, entry) => sum + (Number(entry.lunch_minutes) || 0), 0);
};

/** Map task_id → completed net minutes for Task Log / list views. */
export const completedNetMinutesByTaskId = (checkIns) =>
  completedTimeTotalsByTaskId(checkIns).net;

/** Map task_id → lunch minutes summed across completed cycles (open excluded). */
export const completedLunchMinutesByTaskId = (checkIns) =>
  completedTimeTotalsByTaskId(checkIns).lunch;

/**
 * One pass over completed check-ins: net minutes + lunch minutes per task_id.
 * Open cycles are excluded from both maps.
 */
export const completedTimeTotalsByTaskId = (checkIns) => {
  const net = new Map();
  const lunch = new Map();
  if (!Array.isArray(checkIns)) {
    return { net, lunch };
  }
  for (const entry of checkIns) {
    if (!entry?.check_out_time || entry.task_id == null) {
      continue;
    }
    const taskId = Number(entry.task_id);
    if (!Number.isFinite(taskId)) {
      continue;
    }
    net.set(taskId, (net.get(taskId) || 0) + (segmentNetMinutes(entry) || 0));
    lunch.set(taskId, (lunch.get(taskId) || 0) + (Number(entry.lunch_minutes) || 0));
  }
  return { net, lunch };
};
